import _curry3 from "./internal/_curry3.js";
import adjust from "./adjust.js";
import always from "./always.js";

/**
 * Returns a new copy of the array with the element at the provided index
 * replaced with the given value.
 *
 * When `idx < -list.length || idx >= list.length`, the original list is returned.
 *
 * @func
 * @memberOf R
 * @since v0.14.0
 * @category List
 * @sig Number -> a -> [a] -> [a]
 * @param {Number} idx The index to update.
 * @param {*} x The value to exist at the given index of the returned array.
 * @param {Array|Arguments} list The source array-like object to be updated.
 * @return {Array} A copy of `list` with the value at index `idx` replaced with `x`.
 * @see R.adjust
 * @example
 *
 *      R.update(1, '_', ['a', 'b', 'c']);      //=> ['a', '_', 'c']
 *      R.update(-1, '_', ['a', 'b', 'c']);     //=> ['a', 'b', '_']
 *
 *      // out-of-range returns original list
 *      R.update(3, '_', ['a', 'b', 'c']);      //=> ['a', 'b', 'c']
 *      R.update(-4, '_', ['a', 'b', 'c']);     //=> ['a', 'b', 'c']
 * @symb R.update(-1, a, [b, c]) = [b, a]
 * @symb R.update(0, a, [b, c]) = [a, c]
 * @symb R.update(1, a, [b, c]) = [b, a]
 */
var update = /*#__PURE__*/_curry3(function update(idx, x, list) {
  return adjust(idx, always(x), list);
});
export default update;